/**
 * A short rolling trail of the user's last steps, attached to every error
 * report so a crash tells us HOW they got there — not just what broke.
 *
 * PHI RULE: breadcrumbs carry LABELS ONLY — route paths, control names, API
 * endpoints and status codes. NEVER field values, patient names, DOB or Rx
 * numbers. sanitizeLabel() drops query strings and masks id segments before
 * anything is recorded; the backend re-scrubs as a backstop.
 *
 * Nothing here renders: it is an in-memory array, never shown in the UI and
 * never persisted to storage.
 */

const MAX_ITEMS = 30;
const items = [];

/** Strip identifying parts out of a URL/endpoint label. */
export function sanitizeLabel(raw) {
	let value = String(raw == null ? '' : raw).trim();
	if (!value) {
		return '';
	}

	// Query strings routinely carry patient identifiers — drop them.
	value = value.split('?')[0];

	// Collapse ids so routes group instead of leaking record numbers.
	value = value.replace(
		/\/[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/gi,
		'/:uuid'
	);
	value = value.replace(/\/\d+/g, '/:id');

	// Backstop for anything else that looks personal.
	value = value.replace(/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g, '[redacted]');
	value = value.replace(/\b\d{3}-\d{3}-\d{4}\b/g, '[redacted]');
	value = value.replace(/\b\d{4}-\d{2}-\d{2}\b/g, '[redacted]');
	value = value.replace(/\b\d{9,12}\b/g, '[redacted]');

	value = value.replace(/\s+/g, ' ').trim();
	return value.length > 200 ? `${value.slice(0, 197)}...` : value;
}

function add(type, message) {
	const label = sanitizeLabel(message);
	if (!label) {
		return;
	}

	// Collapse immediate duplicates so the trail stays readable.
	const last = items[items.length - 1];
	if (last && last.t === type && last.m === label) {
		return;
	}

	items.push({ t: type, m: label, at: new Date().toISOString() });
	while (items.length > MAX_ITEMS) {
		items.shift();
	}
}

/** Route/page change. */
export const nav = (url) => add('nav', url);
/** A user action — clicking a control. */
export const action = (label) => add('action', label);
/** An API call: method + endpoint (+ status). */
export const api = (label) => add('api', label);
/** A non-fatal error that preceded the fatal one. */
export const error = (label) => add('error', label);

/** Snapshot for sending with an error report (oldest first). */
export const snapshot = () => items.slice();
export const clear = () => {
	items.length = 0;
};

export const MAX = MAX_ITEMS;

export default { nav, action, api, error, snapshot, clear, sanitizeLabel, MAX: MAX_ITEMS };
